import { calculateAgeFromBirthDate, zipCodeToState } from '@shared';
import {
  AdvisorInfoWithWritingCode,
  checkIfEnrolledInMedicaid,
  formatDateForForm,
  GetAdvisorAddress,
  mapGender,
  mapMaritalStatus,
  returnSafeString,
} from './carrier-utils';

export const SECURIAN_PREFILL_SECTIONS_PRIMARY = [
  'proposed-insured-information',
  'ltc-income-questions',
  'ltc-savings-investments-disclosure',
];

export const getClientUpdatesForSecurian = (clientInfo: Client): PreFillUpdate[] => {
  const clientUpdates = [
    {
      questionRef: 'proposedInsuredName',
      answer: `${clientInfo.clientLastName?.charAt(0).toUpperCase()}${clientInfo.clientLastName?.slice(1)}, ${clientInfo.clientFirstName}`,
    },
    {
      questionRef: 'proposedInsuredEmail',
      answer: clientInfo.clientEmail,
      sourceQuestionRefs: ['clientEmail'],
    },
    {
      questionRef: 'repReportProposedInsuredName',
      answer: `${clientInfo.clientFirstName} ${clientInfo.clientLastName}`,
    },
    {
      questionRef: 'repReportOwnerName',
      answer: `${clientInfo.clientFirstName} ${clientInfo.clientLastName}`,
    },
    {
      questionRef: 'goodHealthApplicantName',
      answer: `${clientInfo.clientFirstName} ${clientInfo.clientLastName}`,
    },
    {
      questionRef: 'policy1InsuredFirstName',
      answer: `${clientInfo.clientFirstName}`,
    },
    {
      questionRef: 'policy1InsuredLastName',
      answer: `${clientInfo.clientLastName}`,
    },
    {
      questionRef: 'jointOwnerPolicyInsured',
      answer: `${clientInfo.clientFirstName} ${clientInfo.clientLastName}`,
    },
  ].filter(update => update.answer);

  const directIntakeMappings = getDirectIntakeMappings(clientInfo);

  return [...clientUpdates, ...directIntakeMappings];
};

const getDirectIntakeMappings = (clientInfo: Client): PreFillUpdate[] => {
  if (clientInfo?.intakeSurvey) {
    const { intakeSurvey } = clientInfo;

    const clientMappings = [
      {
        questionRef: 'proposedInsuredSex',
        answer: mapGender(intakeSurvey.clientGenderAtBirth),
        sourceQuestionRefs: ['clientGenderAtBirth'],
      },
      {
        questionRef: 'proposedInsuredDob',
        answer: formatDateForForm(intakeSurvey?.clientBirthDate),
        sourceQuestionRefs: ['clientBirthDate'],
      },
      {
        questionRef: 'proposedInsuredMaritalStatus',
        answer: mapMaritalStatus(intakeSurvey.clientMaritalStatus, 'securian'),
        sourceQuestionRefs: ['clientMaritalStatus'],
      },
      {
        questionRef: 'proposedInsuredZip',
        answer: intakeSurvey?.clientZipCode,
        sourceQuestionRefs: ['clientZipCode'],
      },
      {
        questionRef: 'proposedInsuredState',
        answer: zipCodeToState(intakeSurvey?.clientZipCode).stateAbbreviation || '',
        sourceQuestionRefs: ['clientZipCode'],
      },
      {
        questionRef: 'proposedInsuredEarnedIncome',
        answer: returnSafeString(intakeSurvey.householdPriorYearIncome),
        sourceQuestionRefs: ['householdPriorYearIncome'],
      },
      {
        questionRef: 'proposedInsuredHouseholdIncome',
        answer: returnSafeString(intakeSurvey.householdPriorYearIncome),
        sourceQuestionRefs: ['householdPriorYearIncome'],
      },
      {
        questionRef: 'proposedInsuredTotalNetWorth',
        answer: returnSafeString(intakeSurvey.householdTotalAssetsValue),
        sourceQuestionRefs: ['householdTotalAssetsValue'],
      },
      {
        questionRef: 'householdAnnualIncome',
        answer:
          typeof intakeSurvey.householdPriorYearIncome === 'number'
            ? intakeSurvey.householdPriorYearIncome < 50000
              ? 'under-50k'
              : intakeSurvey.householdPriorYearIncome < 75000
                ? '50k-75k'
                : intakeSurvey.householdPriorYearIncome < 100000
                  ? '75k-100k'
                  : intakeSurvey.householdPriorYearIncome <= 250000
                    ? '100k-250k'
                    : intakeSurvey.householdPriorYearIncome <= 500000
                      ? '250k-500k'
                      : 'over-500k'
            : '',
        sourceQuestionRefs: ['householdPriorYearIncome'],
      },
      {
        questionRef: 'savingsInvestmentsWorth',
        answer:
          typeof intakeSurvey.householdTotalAssetsValue === 'number'
            ? intakeSurvey.householdTotalAssetsValue < 50000
              ? 'under-50k'
              : intakeSurvey.householdTotalAssetsValue < 75000
                ? '50k-75k'
                : intakeSurvey.householdTotalAssetsValue < 100000
                  ? '75k-100k'
                  : intakeSurvey.householdTotalAssetsValue <= 250000
                    ? '100k-250k'
                    : intakeSurvey.householdTotalAssetsValue <= 500000
                      ? '250k-500k'
                      : 'over-500k'
            : '',
        sourceQuestionRefs: ['householdTotalAssetsValue'],
      },
      {
        questionRef: 'insurableSuitabilityQ8',
        answer: checkIfEnrolledInMedicaid(intakeSurvey.clientHealthPlanList),
        sourceQuestionRefs: ['clientHealthPlanList'],
      },
    ].filter(
      update => update.answer && (Array.isArray(update.answer) ? update.answer.length > 0 : true),
    );
    return clientMappings;
  }
  return [];
};

export const getPolicyUpdatesForSecurian = (
  policyFundingSource: PolicyFundingSource,
  clientInfo?: Client,
): PreFillUpdate[] => {
  let initialQuote = policyFundingSource.updatedQuote || policyFundingSource.sourceQuote;
  if (typeof initialQuote === 'string') {
    try {
      initialQuote = JSON.parse(initialQuote);
    } catch (e) {
      console.error('Failed to parse quote:', e);
      initialQuote = null;
    }
  }

  const isPolicyQuote = (quote: any): quote is PolicyQuote => {
    return quote && typeof quote === 'object' && 'premiumAmount' in quote;
  };

  const sourceQuote = isPolicyQuote(initialQuote) ? initialQuote : null;
  const benefitDurationYears = sourceQuote?.ltcBenefitDurationMonths
    ? sourceQuote.ltcBenefitDurationMonths / 12
    : undefined;
  const productFundingOption = sourceQuote?.productFundingOption.toLowerCase() ?? '';
  const { intakeSurvey } = clientInfo || {};
  const clientAge = intakeSurvey?.clientBirthDate
    ? calculateAgeFromBirthDate(intakeSurvey.clientBirthDate)
    : undefined;

  const policyUpdates = [
    {
      questionRef: 'policyInsuranceAmount',
      answer: returnSafeString(sourceQuote?.deathBenefitInitial),
    },
    {
      questionRef: 'policyBenefitDuration',
      answer: benefitDurationYears
        ? benefitDurationYears == 4
          ? 'four'
          : benefitDurationYears == 5
            ? 'five'
            : benefitDurationYears == 6
              ? 'six'
              : benefitDurationYears == 7
                ? 'seven'
                : benefitDurationYears == 8
                  ? 'eight'
                  : ''
        : '',
    },
    {
      questionRef: 'policyInflationProtection',
      answer: sourceQuote?.inflationBenefitOption
        ? sourceQuote.inflationBenefitOption.toLowerCase().includes('no inflation')
          ? 'reject-all'
          : sourceQuote.inflationBenefitOption.toLowerCase().includes('3') &&
              sourceQuote.inflationBenefitOption.toLowerCase().includes('simple')
            ? '3-simple'
            : sourceQuote.inflationBenefitOption.toLowerCase().includes('3') &&
                sourceQuote.inflationBenefitOption.toLowerCase().includes('compound')
              ? '3-compound'
              : sourceQuote.inflationBenefitOption.toLowerCase().includes('5') &&
                  sourceQuote.inflationBenefitOption.toLowerCase().includes('simple')
                ? '5-simple'
                : sourceQuote.inflationBenefitOption.toLowerCase().includes('5') &&
                    sourceQuote.inflationBenefitOption.toLowerCase().includes('compound')
                  ? '5-compound'
                  : 'reject-all'
        : 'reject-all',
    },
    {
      questionRef: 'premiumMethod',
      answer: productFundingOption.includes('single premium')
        ? 'single'
        : productFundingOption.includes('annual') && productFundingOption.includes('additional')
          ? 'annualFirstYear'
          : productFundingOption.includes('annual') || productFundingOption.includes('year')
            ? 'annual'
            : '',
    },
    {
      questionRef: 'premiumAnnualAmount',
      answer:
        productFundingOption.includes('annual') || productFundingOption.includes('year')
          ? returnSafeString(sourceQuote?.premiumAmount)
          : '',
    },
    {
      questionRef: 'premiumSingleAmount',
      answer: productFundingOption.includes('single premium')
        ? returnSafeString(sourceQuote?.premiumAmount)
        : '',
    },
    {
      questionRef: 'premiumPaymentFrequency',
      answer:
        productFundingOption.includes('annual') || productFundingOption.includes('year')
          ? 'annual'
          : '',
    },
    {
      questionRef: 'ltcPremiumMethod',
      answer: productFundingOption.includes('single premium')
        ? 'single-premium'
        : productFundingOption.includes('annual') && productFundingOption.includes('additional')
          ? 'annual-premium-with-first-year'
          : productFundingOption.includes('annual') || productFundingOption.includes('year')
            ? 'annual-premium'
            : '',
    },
    {
      questionRef: 'ltcAnnualPremiumAmount',
      answer:
        productFundingOption.includes('annual') || productFundingOption.includes('year')
          ? returnSafeString(sourceQuote?.premiumAmount)
          : '',
    },
    {
      questionRef: 'ltcSinglePremiumAmount',
      answer: productFundingOption.includes('single premium')
        ? returnSafeString(sourceQuote?.premiumAmount)
        : '',
    },
    {
      questionRef: 'incomeBuyInflationProtection',
      answer: sourceQuote?.inflationBenefitOption
        ? sourceQuote.inflationBenefitOption.toLowerCase().includes('no inflation')
          ? 'no'
          : 'yes'
        : '',
    },
    {
      questionRef: 'ltcAnnualPremiumYears',
      answer:
        productFundingOption.includes('year') && productFundingOption.includes('10') ? '10' : '',
    },
    {
      questionRef: 'requestedPolicyLtcBenifitPool',
      answer: returnSafeString(sourceQuote?.ltcBenefitTotal),
    },
    {
      questionRef: 'riderRequestedTotalLtcBenefit',
      answer: returnSafeString(sourceQuote?.ltcBenefitTotal),
    },
    {
      questionRef: 'riderRequestedTotalDeathBenefit',
      answer: returnSafeString(sourceQuote?.deathBenefitInitial),
    },
    {
      questionRef: 'requestedPolicyOriginalBaseMonthlyBenefit',
      answer: returnSafeString(sourceQuote?.ltcBenefitMonthlyIndividual),
    },
    {
      questionRef: 'riderRequestedMonthlyBaseBenifit',
      answer: returnSafeString(sourceQuote?.ltcBenefitMonthlyIndividual),
    },
    {
      questionRef: 'requestedPolicyYearsLtcCoverage',
      answer: returnSafeString((sourceQuote?.ltcBenefitDurationMonths ?? 0) / 12),
    },
    {
      questionRef: 'riderRequestedNumOfLtcYears',
      answer: returnSafeString((sourceQuote?.ltcBenefitDurationMonths ?? 0) / 12),
    },
    {
      questionRef: 'requestedPolicyInflationBenifits',
      answer: sourceQuote?.inflationBenefitOption
        ? sourceQuote.inflationBenefitOption.toLowerCase().includes('no inflation')
          ? 'no'
          : sourceQuote.inflationBenefitOption.toLowerCase().includes('simple')
            ? 'simple'
            : sourceQuote.inflationBenefitOption.toLowerCase().includes('compound')
              ? 'compound'
              : 'no'
        : 'no',
    },
    {
      questionRef: 'requestedPolicySimpleInflationBenefit',
      answer: sourceQuote?.inflationBenefitOption
        ? sourceQuote.inflationBenefitOption.toLowerCase().includes('no inflation')
          ? '0'
          : sourceQuote.inflationBenefitOption.toLowerCase().includes('3')
            ? '3'
            : sourceQuote.inflationBenefitOption.toLowerCase().includes('5')
              ? '5'
              : '0'
        : '0',
    },
    {
      questionRef: 'requestedPolicyCompoundInflationBenefit',
      answer: sourceQuote?.inflationBenefitOption
        ? sourceQuote.inflationBenefitOption.toLowerCase().includes('no inflation')
          ? '0'
          : sourceQuote.inflationBenefitOption.toLowerCase().includes('3')
            ? '3'
            : sourceQuote.inflationBenefitOption.toLowerCase().includes('5')
              ? '5'
              : '0'
        : '0',
    },
    {
      questionRef: 'riderRequestedInflationBenefitType',
      answer: sourceQuote?.inflationBenefitOption
        ? sourceQuote.inflationBenefitOption.toLowerCase().includes('no inflation')
          ? 'none'
          : sourceQuote.inflationBenefitOption.toLowerCase().includes('simple')
            ? 'simple'
            : sourceQuote.inflationBenefitOption.toLowerCase().includes('compound')
              ? 'compound'
              : 'none'
        : 'none',
    },
    {
      questionRef: 'riderRequestedSimpleInflationPercent',
      answer: sourceQuote?.inflationBenefitOption
        ? sourceQuote.inflationBenefitOption.toLowerCase().includes('no inflation')
          ? '0'
          : sourceQuote.inflationBenefitOption.toLowerCase().includes('3')
            ? '3'
            : sourceQuote.inflationBenefitOption.toLowerCase().includes('5')
              ? '5'
              : '0'
        : '0',
    },
    {
      questionRef: 'riderRequestedCompoundInflationPercent',
      answer: sourceQuote?.inflationBenefitOption
        ? sourceQuote.inflationBenefitOption.toLowerCase().includes('no inflation')
          ? '0'
          : sourceQuote.inflationBenefitOption.toLowerCase().includes('3')
            ? '3'
            : sourceQuote.inflationBenefitOption.toLowerCase().includes('5')
              ? '5'
              : '0'
        : '0',
    },
    {
      questionRef: 'requestedPolicyPremium',
      answer: productFundingOption.includes('single premium')
        ? 'single'
        : productFundingOption.includes('annual') || productFundingOption.includes('year')
          ? 'annual'
          : '',
    },
    {
      questionRef: 'requestedPolicyAnnualPremium',
      answer:
        productFundingOption.includes('annual') || productFundingOption.includes('year')
          ? returnSafeString(sourceQuote?.premiumAmount)
          : '',
    },
    {
      questionRef: 'requestedPolicySinglePremium',
      answer: productFundingOption.includes('single premium')
        ? returnSafeString(sourceQuote?.premiumAmount)
        : '',
    },
    {
      questionRef: 'riderRequestedPremiumType',
      answer: productFundingOption.includes('single premium')
        ? 'single-premium'
        : productFundingOption.includes('annual') || productFundingOption.includes('year')
          ? 'annual-premium'
          : '',
    },
    {
      questionRef: 'riderRequestedPremiumAnnualAmount',
      answer:
        productFundingOption.includes('annual') || productFundingOption.includes('year')
          ? returnSafeString(sourceQuote?.premiumAmount)
          : '',
    },
    {
      questionRef: 'riderRequestedPremiumSingleAmount',
      answer: productFundingOption.includes('single premium')
        ? returnSafeString(sourceQuote?.premiumAmount)
        : '',
    },
    {
      questionRef: 'riderRequestedYearsPremiumPayment',
      answer: productFundingOption.includes('single premium') ? 'single' : '',
    },
    {
      questionRef: 'requestedPolicyYearsOfPremium',
      answer: productFundingOption.includes('single premium')
        ? 'single'
        : productFundingOption.includes('10')
          ? '10'
          : '',
    },
    {
      questionRef: 'policyReturnOfPremium',
      answer: 'ltc-boost',
    },
    {
      questionRef: 'ivesTaxPayerPreQuestion',
      answer:
        clientAge && sourceQuote?.deathBenefitInitial
          ? (clientAge >= 70 && sourceQuote?.deathBenefitInitial > 3000000) ||
            (clientAge < 70 && sourceQuote?.deathBenefitInitial > 5000000)
            ? 'yes'
            : 'no'
          : '',
      sourceQuestionRefs: ['clientBirthDate'],
    },
  ].filter(update => update.answer);

  return policyUpdates;
};

export const getAdvisorUpdatesForSecurian = (
  advisorInfo: AdvisorInfoWithWritingCode,
): PreFillUpdate[] => {
  return [
    {
      questionRef: 'licensedFpName',
      answer: `${advisorInfo.advisorFirstName} ${advisorInfo.advisorLastName}`,
    },
    {
      questionRef: 'licensedFpFirmCode',
      answer: advisorInfo.bgaWritingCode || '',
    },
    {
      questionRef: 'firmRepCode',
      answer: advisorInfo.bgaWritingCode || '',
    },
    {
      questionRef: 'repReportServicingRepFirmCode',
      answer: advisorInfo.bgaWritingCode || '',
    },
    {
      questionRef: 'repReportServicingRepName',
      answer: `${advisorInfo.advisorFirstName} ${advisorInfo.advisorLastName}`,
    },
    {
      questionRef: 'nrpAgentAddress',
      answer: GetAdvisorAddress(advisorInfo.advisorBusinessAddress, true),
    },
    {
      questionRef: 'paFinancialProfessionalName',
      answer: `${advisorInfo.advisorFirstName} ${advisorInfo.advisorLastName}`,
    },
    {
      questionRef: 'paFinancialProfessionalAddress',
      answer: GetAdvisorAddress(advisorInfo.advisorBusinessAddress, true),
    },
    {
      questionRef: 'moAgentNameAndAddress',
      answer: `${advisorInfo.advisorFirstName} ${advisorInfo.advisorLastName} ${GetAdvisorAddress(advisorInfo.advisorBusinessAddress, true)}`,
    },
    {
      questionRef: 'moAgentPhoneNumber',
      answer: advisorInfo.advisorBusinessPhoneNumber || advisorInfo.advisorPhoneNumber || '',
    },
    {
      questionRef: 'moAgentLicenseNumber',
      answer: advisorInfo.bgaWritingCode || '',
    },
    {
      questionRef: 'gaAgentName',
      answer: `${advisorInfo.advisorFirstName} ${advisorInfo.advisorLastName}`,
    },
    {
      questionRef: 'gaAgentNameAndAddress',
      answer: `${advisorInfo.advisorFirstName} ${advisorInfo.advisorLastName} ${GetAdvisorAddress(advisorInfo.advisorBusinessAddress, true)}`,
    },
    {
      questionRef: 'gaAgentTelephoneAndLicense',
      answer: `${advisorInfo.advisorBusinessPhoneNumber || advisorInfo.advisorPhoneNumber || ''} ${advisorInfo.bgaWritingCode || ''}`,
    },
  ].filter(update => update.answer);
};
